import { Box, Button, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
// @ts-expect-error - No types available for this package
import { CrestLoader } from '@scoir/college-image-components'

import sanitize from 'common/sanitize-html-helper'
import { useAppSelector } from 'store'
import { isDemoCollegeNameAsScoirEnabled } from 'store/feature-toggle'

import { Message } from '../types'

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  collegeHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    wordWrap: 'break-word',
    marginBottom: theme.spacing(2),
  },
  collegeCrest: {
    width: '50px',
    height: '50px',
    marginRight: '10px',
  },
  link: {
    textAlign: 'start',
    margin: '20px 0',
  },
  ctaButton: {
    maxWidth: '60%',
    whiteSpace: 'pre-wrap',
  },
  editModeBorder: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
}))

const MessageDetails = ({ message, inEditMode }: { message: Message; inEditMode?: boolean }) => {
  const classes = useStyles()
  const demoEnabled = useAppSelector(isDemoCollegeNameAsScoirEnabled)
  const link = message.Link?.trim()
  const linkLabel = message.LinkLabel
  const scid = demoEnabled ? 6900000 : message.Scid
  const collegeName = demoEnabled ? 'Scoir University' : message.CollegeName
  const collegeLocation = demoEnabled
    ? 'West Chester, PA, United States'
    : [message.City, message.State, message.Country]
        .filter((value) => value != null && value !== '') // removes null, undefined, and empty strings
        .join(', ')

  const hasPersonalizationTokens = /{{(.*?)}}/.test(message.Subject || '') || /{{(.*?)}}/.test(message.Message || '')
  const replacePersonalizationTokens = (text: string) => {
    return text.replace(/\{\{(.*?)\}\}/g, (_, token) => {
      switch (token.trim()) {
        case 'Preferred_Name':
          return 'Alex'
        default:
          return `{{${token}}}`
      }
    })
  }
  const messageSubject = replacePersonalizationTokens(message.Subject)
  const messageBody = sanitize(replacePersonalizationTokens(message.Message))

  return (
    <>
      {hasPersonalizationTokens && (
        <Box mb={2}>
          <Alert severity="info">
            Personalization tokens (like {`{{ Preferred_Name }}`}) are shown with placeholder values (e.g., "Alex").
            Recipients will see their actual data when the message is sent.
          </Alert>
        </Box>
      )}
      <Paper
        elevation={0}
        className={`${classes.paper} ${inEditMode ? classes.editModeBorder : ''}`}
      >
        <div className={classes.collegeHeader}>
          {scid && (
            <CrestLoader
              className={classes.collegeCrest}
              scid={scid}
            />
          )}
          <div>
            <Typography
              variant="body1"
              color="textPrimary"
            >
              {collegeName}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              {collegeLocation}
            </Typography>
          </div>
        </div>
        <Typography variant="h6">{messageSubject}</Typography>
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{ __html: messageBody }}
        />
        {link && (
          <div className={classes.link}>
            <Button
              className={classes.ctaButton}
              variant="contained"
              color="primary"
              component="a"
              href={link}
              rel="noopener noreferrer"
              target="_blank"
            >
              {linkLabel || 'Learn More'}
            </Button>
          </div>
        )}
      </Paper>
    </>
  )
}

export default MessageDetails
